import { useEffect, useMemo, useState } from 'react';
import { Download, Eye, FileSignature, Mail, RefreshCw } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Input,
  Spinner,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { EmptyState, StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { formatDateTime } from '@/lib/format';
import {
  useDocumentMailPrefill,
  useResignDocument,
  useSendDocumentMail,
  useSignDocument,
  useSignedDocument,
  useSignedDocumentPreview,
  type DocumentType,
  type ImageSource,
  type SignedDocumentStatus,
} from '@/api/esign-documents';

/** Map eSign status → @ui StatusPill tone. Missing status → not-created (neutral). */
function statusTone(status?: SignedDocumentStatus | '' | null): StatusTone {
  switch (status) {
    case 'signed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'muted';
    default:
      return 'neutral';
  }
}

/** Human label for an eSign status. */
function statusLabel(status?: SignedDocumentStatus | '' | null): string {
  switch (status) {
    case 'signed':
      return 'Đã ký';
    case 'pending':
      return 'Chờ ký';
    case 'failed':
      return 'Lỗi';
    case 'cancelled':
      return 'Đã huỷ';
    default:
      return 'Chưa tạo';
  }
}

/** Parse a comma/newline-separated address string into a trimmed, non-empty list. */
function parseAddresses(value: string): string[] {
  return value
    .split(/[,\n;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

interface SendMailDialogProps {
  invoiceId: number;
  docType: DocumentType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Send-mail form dialog — prefilled from `mail-prefill`, posts via send-mail. */
function SendMailDialog({ invoiceId, docType, open, onOpenChange }: SendMailDialogProps) {
  const toast = useToast();
  const { data: prefill, isLoading: prefillLoading } = useDocumentMailPrefill(invoiceId, docType, {
    enabled: open,
  });
  const sendMail = useSendDocumentMail(invoiceId, docType);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [touched, setTouched] = useState(false);

  // Seed from prefill once per open; preserve user edits afterwards.
  useEffect(() => {
    if (!open) {
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setTouched(false);
      return;
    }
    if (!prefill || touched) return;
    setTo((prefill.to ?? []).join(', '));
    setCc((prefill.cc ?? []).join(', '));
    setBcc((prefill.bcc ?? []).join(', '));
    setSubject(prefill.subject ?? '');
  }, [open, prefill, touched]);

  const recipients = parseAddresses(to);

  const onSend = () => {
    if (recipients.length === 0) return;
    sendMail.mutate(
      {
        to: recipients,
        cc: parseAddresses(cc),
        bcc: parseAddresses(bcc),
        subject: subject.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Đã gửi email');
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          toast.error('Gửi email thất bại', {
            description: err instanceof Error ? err.message : undefined,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Gửi email tài liệu</DialogTitle>
          <DialogDescription>
            Gửi tài liệu đã ký cho khách hàng. Ngăn cách nhiều địa chỉ bằng dấu phẩy.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {prefillLoading ? (
            <Alert tone="info" title="Đang tải thông tin người nhận…" />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field label="Người nhận (To)" required>
                {(p) => (
                  <Input
                    {...p}
                    value={to}
                    onChange={(e) => {
                      setTouched(true);
                      setTo(e.target.value);
                    }}
                    placeholder="name@customer.com"
                  />
                )}
              </Field>
              <Field label="CC">
                {(p) => (
                  <Input
                    {...p}
                    value={cc}
                    onChange={(e) => {
                      setTouched(true);
                      setCc(e.target.value);
                    }}
                    placeholder="cc@customer.com"
                  />
                )}
              </Field>
              <Field label="BCC">
                {(p) => (
                  <Input
                    {...p}
                    value={bcc}
                    onChange={(e) => {
                      setTouched(true);
                      setBcc(e.target.value);
                    }}
                    placeholder="bcc@customer.com"
                  />
                )}
              </Field>
              <Field label="Tiêu đề">
                {(p) => (
                  <Input
                    {...p}
                    value={subject}
                    onChange={(e) => {
                      setTouched(true);
                      setSubject(e.target.value);
                    }}
                    placeholder="Tiêu đề email"
                  />
                )}
              </Field>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Huỷ
          </Button>
          <Button
            startIcon={<Mail size={15} />}
            loading={sendMail.isPending}
            disabled={recipients.length === 0}
            onClick={onSend}
          >
            Gửi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ResignDialogProps {
  invoiceId: number;
  docType: DocumentType;
  imageSource: ImageSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Re-sign dialog — requires a reason, then cancels + re-signs current data. */
function ResignDialog({ invoiceId, docType, imageSource, open, onOpenChange }: ResignDialogProps) {
  const toast = useToast();
  const resign = useResignDocument(invoiceId, docType);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const onConfirm = () => {
    if (!reason.trim()) return;
    resign.mutate(
      { reason: reason.trim(), image_source: imageSource },
      {
        onSuccess: () => {
          toast.success('Đã gửi yêu cầu ký lại');
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          toast.error('Ký lại thất bại', {
            description: err instanceof Error ? err.message : undefined,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Ký lại tài liệu</DialogTitle>
          <DialogDescription>
            Huỷ bản đã ký hiện tại và ký lại theo dữ liệu hoá đơn hiện tại. Nhập lý do.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Lý do" required>
            {(p) => (
              <Textarea
                {...p}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Lý do ký lại"
                rows={3}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Huỷ
          </Button>
          <Button
            variant="danger"
            startIcon={<RefreshCw size={15} />}
            loading={resign.isPending}
            disabled={!reason.trim()}
            onClick={onConfirm}
          >
            Ký lại
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface InvoiceSignedDocumentTabProps {
  invoiceId: number;
  docType: DocumentType;
}

/**
 * Shared eSign-document tab for an invoice. Renders the document's sign status,
 * requested/signed metadata, action buttons (Ký số / Ký lại / Gửi mail — each
 * behind a confirm step the USER triggers), a download-signed-PDF button, an
 * auth-fetched HTML preview (iframe srcDoc), and an error alert on failure.
 * Shows a clean EmptyState when the document was never created.
 *
 * Only reconciliation-statement varies by image source; payment-request pins
 * it to 'auto'.
 */
export function InvoiceSignedDocumentTab({ invoiceId, docType }: InvoiceSignedDocumentTabProps) {
  const imageSource: ImageSource = 'auto';

  const { data: doc, isLoading, isError, refetch } = useSignedDocument(invoiceId, docType);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [resignOpen, setResignOpen] = useState(false);
  const [sendMailOpen, setSendMailOpen] = useState(false);

  const toast = useToast();
  const sign = useSignDocument(invoiceId, docType);

  const preview = useSignedDocumentPreview(invoiceId, docType, imageSource, {
    enabled: previewOpen,
  });

  const status = doc?.status || undefined;
  const notCreated = !doc || !status;
  const isSigned = status === 'signed';
  const isFailed = status === 'failed';

  const onSign = () => {
    sign.mutate(
      { image_source: imageSource },
      {
        onSuccess: () => {
          toast.success('Đã gửi yêu cầu ký số');
          setSignOpen(false);
        },
        onError: (err: unknown) => {
          toast.error('Ký số thất bại', {
            description: err instanceof Error ? err.message : undefined,
          });
        },
      },
    );
  };

  const infoItems = useMemo(
    () => [
      { label: 'Trạng thái', value: statusLabel(status) },
      { label: 'Người yêu cầu', value: doc?.requested_by_name || '—' },
      { label: 'Thời điểm yêu cầu', value: formatDateTime(doc?.requested_at) },
      { label: 'Thời điểm ký', value: formatDateTime(doc?.signed_at) },
      { label: 'Gửi mail gần nhất', value: formatDateTime(doc?.last_sent_at) },
      {
        label: 'URL hết hạn',
        value: formatDateTime(doc?.file_url_expires_at),
      },
    ],
    [doc, status],
  );

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Không tải được tài liệu"
        actions={
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Thử lại
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--vn-space-8)' }}>
        <Spinner />
      </div>
    );
  }

  if (notCreated) {
    return (
      <>
        <Card>
          <CardBody>
            <EmptyState
              icon={<FileSignature size={28} />}
              title="Chưa tạo"
              description="Tài liệu chưa được tạo cho hoá đơn này. Bấm Ký số để tạo."
              actions={
                <Button startIcon={<FileSignature size={15} />} onClick={() => setSignOpen(true)}>
                  Ký số
                </Button>
              }
            />
          </CardBody>
        </Card>
        <ConfirmDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          title="Ký số tài liệu"
          description="Gửi tài liệu lên MISA eSign để ký. Trạng thái sẽ là 'Chờ ký' cho tới khi người ký duyệt trong ứng dụng MISA."
          confirmLabel="Ký số"
          loading={sign.isPending}
          onConfirm={onSign}
        />
      </>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-3)',
                flexWrap: 'wrap',
              }}
            >
              <StatusPill tone={statusTone(status)} loading={status === 'pending'}>
                {statusLabel(status)}
              </StatusPill>
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  startIcon={<Eye size={15} />}
                  onClick={() => setPreviewOpen((v) => !v)}
                >
                  {previewOpen ? 'Ẩn xem trước' : 'Xem trước'}
                </Button>
                {isSigned && doc?.file_download_url ? (
                  <Button
                    variant="secondary"
                    startIcon={<Download size={15} />}
                    onClick={() =>
                      window.open(doc.file_download_url as string, '_blank', 'noopener,noreferrer')
                    }
                  >
                    Tải PDF đã ký
                  </Button>
                ) : null}
                {isSigned ? (
                  <Button
                    variant="secondary"
                    startIcon={<Mail size={15} />}
                    onClick={() => setSendMailOpen(true)}
                  >
                    Gửi mail
                  </Button>
                ) : null}
                {isSigned ? (
                  <Button
                    variant="secondary"
                    startIcon={<RefreshCw size={15} />}
                    onClick={() => setResignOpen(true)}
                  >
                    Ký lại
                  </Button>
                ) : (
                  <Button
                    startIcon={<FileSignature size={15} />}
                    onClick={() => setSignOpen(true)}
                  >
                    Ký số
                  </Button>
                )}
              </div>
            </div>

            {isFailed && doc?.error_message ? (
              <Alert tone="danger" title="Ký số thất bại">
                {doc.error_message}
              </Alert>
            ) : null}

            <InfoGrid items={infoItems} />
          </div>
        </CardBody>
      </Card>

      {previewOpen ? (
        <Card>
          <CardBody>
            {preview.isLoading ? (
              <div
                style={{ display: 'flex', justifyContent: 'center', padding: 'var(--vn-space-8)' }}
              >
                <Spinner />
              </div>
            ) : preview.isError ? (
              <Alert tone="danger" title="Không tải được bản xem trước" />
            ) : (
              <iframe
                title="Document preview"
                srcDoc={preview.data ?? ''}
                sandbox=""
                style={{
                  width: '100%',
                  height: '75vh',
                  border: '1px solid var(--vn-border-subtle)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: 'var(--vn-bg-canvas)',
                }}
              />
            )}
          </CardBody>
        </Card>
      ) : null}

      <ConfirmDialog
        open={signOpen}
        onOpenChange={setSignOpen}
        title="Ký số tài liệu"
        description="Gửi tài liệu lên MISA eSign để ký. Trạng thái sẽ là 'Chờ ký' cho tới khi người ký duyệt trong ứng dụng MISA."
        confirmLabel="Ký số"
        loading={sign.isPending}
        onConfirm={onSign}
      />

      <ResignDialog
        invoiceId={invoiceId}
        docType={docType}
        imageSource={imageSource}
        open={resignOpen}
        onOpenChange={setResignOpen}
      />

      <SendMailDialog
        invoiceId={invoiceId}
        docType={docType}
        open={sendMailOpen}
        onOpenChange={setSendMailOpen}
      />
    </div>
  );
}
